
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto, ImageContainerDimensionsRestDto} from "@/api/pharma-cms-content-block/models";
import {
  ImageContainerDimensionsRestDtoModel
} from "@/models/api/pharma-cms-content-block/ImageContainerDimensionsRestDtoModel";
import {ObjectHorizontalPositionEnum} from "@/api/enums/object-horizontal-position-enum";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import InputNumber from "@/components/UI/InputNumber.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import Subtitle from "@/components/UI/Subtitle.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";

// Smallest storable width percentage; anything lower means "full available width".
const MIN_WIDTH_PERCENTAGE = 10 as number;
// At full width the container fills its column, so there is nothing to align it against.
const FULL_WIDTH_PERCENTAGE = 100 as number;

export default defineComponent({
  name: "ImageContainerDimensionsDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, InputNumber, InputSelect, CssDimensionEditor, MetadataGroupCard, Subtitle},
  emits: ['update:modelValue'],
  props: {
    // Null means the group is switched off: the image container uses SMA defaults.
    modelValue: {
      type: Object as PropType<ImageContainerDimensionsRestDto | null>,
      required: false,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.persistedDto = this.modelValue ? ImageContainerDimensionsRestDtoModel.cloneFrom(this.modelValue) : null;

    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: ImageContainerDimensionsRestDtoModel.createWithDefaults() as ImageContainerDimensionsRestDto,
      lastActiveDto: null as ImageContainerDimensionsRestDto | null,
      persistedDto: null as ImageContainerDimensionsRestDto | null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: ImageContainerDimensionsRestDto | null) {
        this.enabled = !!newValue;
        if (newValue) {
          this.localDto = ImageContainerDimensionsRestDtoModel.cloneFrom(newValue);
        }
      },
    },
  },
  computed: {
    // An empty field means "no width limit"; it is shown empty rather than as 0.
    widthPercentage: {
      get(): number | null {
        return this.localDto.width_percentage ?? null;
      },
      // Clamping here would rewrite the field on every keystroke: typing the "3" of
      // "30" would immediately jump to the minimum. The range is validated instead.
      // An emptied field arrives as NaN.
      set(newValue: number | null) {
        this.localDto.width_percentage = (newValue !== null && Number.isFinite(newValue)) ? newValue : null;

        this.clearAlignmentWhenIrrelevant();
      },
    },
    MIN_WIDTH_PERCENTAGE(): number {
      return MIN_WIDTH_PERCENTAGE;
    },
    // The alignment select is hidden rather than disabled: a visible control with no
    // effect on the rendered page is worse than no control at all.
    showAlignment(): boolean {
      return this.isAlignmentRelevant();
    },
    enumPositionHorizontal(): CodeDescriptionRestDto[] {
      return [
        {code: ObjectHorizontalPositionEnum.LEFT, description: this.$t('left')},
        {code: ObjectHorizontalPositionEnum.CENTER, description: this.$t('center')},
        {code: ObjectHorizontalPositionEnum.RIGHT, description: this.$t('right')},
      ];
    },
    aspectRatioWidth: {
      get(): number | null {
        return this.localDto.aspect_ratio_width ?? null;
      },
      set(newValue: number | null) {
        this.localDto.aspect_ratio_width = newValue ? newValue : null;
      },
    },
    aspectRatioHeight: {
      get(): number | null {
        return this.localDto.aspect_ratio_height ?? null;
      },
      set(newValue: number | null) {
        this.localDto.aspect_ratio_height = newValue ? newValue : null;
      },
    },
    // Both components of the ratio must be set together, or both left empty.
    aspectRatioWidthRules(): string | undefined {
      return this.localDto.aspect_ratio_height ? 'required' : undefined;
    },
    aspectRatioHeightRules(): string | undefined {
      return this.localDto.aspect_ratio_width ? 'required' : undefined;
    },
  },
  methods: {
    isAlignmentRelevant(): boolean {
      const width = this.localDto.width_percentage;

      return width !== null && width !== undefined && width < FULL_WIDTH_PERCENTAGE;
    },
    // Never persist an alignment the renderer would ignore; leaving it set would also
    // resurface it if the editor narrows the container again later.
    clearAlignmentWhenIrrelevant(): void {
      if (!this.isAlignmentRelevant()) {
        this.localDto.alignment_code = null;
      }
    },
    onEnabledChanged(newValue: boolean): void {
      // The switch re-emits its current state on render; acting on that would
      // bounce an update back up and re-derive `enabled` again, without end.
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.lastActiveDto
          ? ImageContainerDimensionsRestDtoModel.cloneFrom(this.lastActiveDto)
          : ImageContainerDimensionsRestDtoModel.createWithDefaults();

        this.$emit('update:modelValue', this.localDto);
      } else {
        this.lastActiveDto = ImageContainerDimensionsRestDtoModel.cloneFrom(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.persistedDto
        ? ImageContainerDimensionsRestDtoModel.cloneFrom(this.persistedDto)
        : ImageContainerDimensionsRestDtoModel.createWithDefaults();

      this.$emit('update:modelValue', this.localDto);
    },
    // A max height of 0 imposes no limit at all, which is what an absent value means.
    // An empty field arrives as null or NaN and means the same thing.
    normalizeNeutralMaxHeight(): void {
      const maxHeight = this.localDto.max_height;

      if (maxHeight && !(maxHeight.unit_dimension.value > 0)) {
        this.localDto.max_height = null;
      }
    },
    emitModelChanged(): void {
      this.normalizeNeutralMaxHeight();

      this.$emit('update:modelValue', this.localDto);
    },
  },
});
